import { useState, type MouseEvent, type ReactNode } from 'react';
import { VestingBar } from '@gonka-wallet/ui-tokens';

export interface ExpandedWalletRowProps {
  readonly id: string;
  readonly name: string;
  readonly address: string;
  /** Pre-formatted total balance (e.g. "186,097.4400"). Mono rendering. */
  readonly total: ReactNode;
  /** Wallet glyph rendered left of name (36px). */
  readonly glyph: ReactNode;
  /** Unlocked portion (mono-formatted). Omit if fully unlocked. */
  readonly unlocked?: ReactNode;
  /** Vesting portion (mono-formatted). Omit if fully unlocked. */
  readonly vesting?: ReactNode;
  /** Display like "Jun 12" — next tranche unlock. */
  readonly nextUnlockDate?: string;
  /** Days left until next unlock. */
  readonly daysLeft?: number;
  /** `true` = this wallet is the active miner (badge "Active"). */
  readonly active?: boolean;
  /** `true` = watch-only address (badge "Watch", no action slot). */
  readonly watch?: boolean;
  readonly open: boolean;
  readonly onToggle: () => void;
  /** Send/Receive buttons row. Not shown for watch=true. */
  readonly injectActions?: ReactNode;
  /** Localized copy. */
  readonly labels?: {
    readonly unlockedLabel?: string;
    readonly vestingLabel?: string;
    readonly totalLabel?: string;
    readonly nextUnlockLabel?: (date: string, daysLeft: number) => string;
    readonly watchOnlyHint?: string;
    readonly importCta?: string;
    readonly activeLabel?: string;
    readonly watchLabel?: string;
    readonly vestingChipLabel?: string;
  };
  /** Called when user taps "Import phrase" on watch-only row. */
  readonly onImportRequest?: () => void;
  readonly className?: string;
}

const DEFAULT_LABELS = {
  unlockedLabel: 'Unlocked',
  vestingLabel: 'Vesting',
  totalLabel: 'Total',
  nextUnlockLabel: (date: string, daysLeft: number): string =>
    `Next unlock · ${date} · in ${daysLeft}d`,
  watchOnlyHint: 'Import phrase to use',
  importCta: 'Import phrase',
  activeLabel: 'Current',
  watchLabel: 'Watch',
  vestingChipLabel: 'Vesting',
} as const;

/**
 * Accordion wallet row. Three expand modes:
 *   1. with vesting — Unlocked/Vesting/Total grid + VestingBar + next unlock +
 *      dashed divider + injected Send/Receive.
 *   2. fully unlocked — no vesting rows, just dashed divider + actions.
 *   3. watch-only — dashed divider + watch-only hint + "Import →" link.
 *
 * Collapsed header: glyph (36px) + name + short address + total amount on
 * the right. Active/Watch badges on the right when present.
 *
 * Based on docs/design_handoff/.../wallet-detail-variants.jsx.
 */
export function ExpandedWalletRow({
  name,
  address,
  total,
  glyph,
  unlocked,
  vesting,
  nextUnlockDate,
  daysLeft,
  active,
  watch,
  open,
  onToggle,
  injectActions,
  labels,
  onImportRequest,
  className = '',
}: ExpandedWalletRowProps): ReactNode {
  const L = { ...DEFAULT_LABELS, ...(labels ?? {}) };
  const hasVesting = unlocked !== undefined && vesting !== undefined;

  const shortAddr = `${address.slice(0, 9)}…${address.slice(-4)}`;
  const MAX_NAME = 10;
  const displayName = name.length > MAX_NAME ? `${name.slice(0, MAX_NAME)}…` : name;

  const unlockedRatio =
    hasVesting && typeof unlocked === 'string' && typeof vesting === 'string'
      ? safeRatio(unlocked, vesting)
      : 1;

  return (
    <div
      className={[
        'rounded-2xl border bg-surface-1 transition-colors duration-base ease-standard',
        open
          ? 'border-border-strong shadow-[0_2px_14px_rgba(0,0,0,0.04)]'
          : 'border-border-hairline',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        className="grid w-full cursor-pointer grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-x-3 px-4 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-link)]"
      >
        <div className="h-9 w-9 shrink-0">{glyph}</div>
        <div className="flex min-w-0 flex-col">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-2 w-2 shrink-0 items-center justify-center">
              {active && (
                <span
                  aria-label={L.activeLabel}
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: 'var(--color-pos)' }}
                />
              )}
              {watch && (
                <span
                  aria-label={L.watchLabel}
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: 'var(--color-text-tertiary)' }}
                />
              )}
            </span>
            <span className="inline-block w-[6.25rem] truncate text-[14px] font-semibold leading-[1.1] tracking-[-0.2px] text-text-primary">
              {displayName}
            </span>
          </div>
          <div className="mt-[5px] flex min-w-0 items-center gap-1.5">
            <span className="truncate font-mono text-[11px] leading-[1.1] tracking-[0.2px] text-text-tertiary tabular-nums">
              {shortAddr}
            </span>
            <CopyButton text={address} />
          </div>
        </div>
        <div className="flex flex-col items-end gap-[3px]">
          <div className="text-right font-mono text-[14px] font-medium leading-none text-text-primary tabular-nums">
            {total}
          </div>
          {hasVesting && (
            <span className="text-[9px] font-bold uppercase tracking-[1.2px] text-warning">
              {L.vestingChipLabel}
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4">
          {hasVesting && (
            <>
              <div className="grid grid-cols-3 gap-2 rounded-xl bg-surface-2 px-3 py-2 text-center">
                <MiniStat label={L.unlockedLabel} value={unlocked} tone="pos" />
                <MiniStat label={L.vestingLabel} value={vesting} tone="warning" />
                <MiniStat label={L.totalLabel} value={total} tone="default" />
              </div>
              <VestingBar
                unlocked={unlockedRatio}
                unlockedAmount={unlocked}
                vestingAmount={vesting}
                labels={{ unlocked: '', vesting: '' }}
              />
              {nextUnlockDate !== undefined && daysLeft !== undefined && (
                <div className="mt-3 text-[11px] text-text-tertiary">
                  {L.nextUnlockLabel(nextUnlockDate, daysLeft)}
                </div>
              )}
            </>
          )}

          <DashedDivider />

          {watch ? (
            <div className="flex items-center justify-between pt-3">
              <span className="text-[11px] text-text-tertiary">{L.watchOnlyHint}</span>
              {onImportRequest && (
                <button
                  type="button"
                  onClick={onImportRequest}
                  className="text-[11px] font-medium text-link hover:opacity-80"
                >
                  {L.importCta}
                </button>
              )}
            </div>
          ) : (
            <div className="pt-3">{injectActions}</div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone: 'default' | 'pos' | 'warning';
}): ReactNode {
  const toneCls =
    tone === 'pos'
      ? 'text-pos'
      : tone === 'warning'
        ? 'text-warning'
        : 'text-text-primary';
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
        {label}
      </div>
      <div
        className={['mt-0.5 font-mono text-[12px] font-medium tabular-nums', toneCls].join(' ')}
      >
        {value}
      </div>
    </div>
  );
}


function CopyButton({ text }: { text: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  const onCopy = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      void navigator.clipboard?.writeText(text);
    } catch {
      /* ignore — clipboard may be unavailable */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={copied ? 'Copied' : 'Copy address'}
      className={[
        'inline-grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded border-0 bg-transparent p-0 transition-colors',
        copied ? 'text-text-primary' : 'text-text-tertiary hover:text-text-secondary',
      ].join(' ')}
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path
            d="M2.5 6.5 L5 9 L9.5 3.5"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
          <rect
            x="3.5"
            y="3.5"
            width="6"
            height="6"
            rx="1.2"
            stroke="currentColor"
            strokeWidth={1.2}
          />
          <path
            d="M2 7.5 V2.8 A0.8 0.8 0 0 1 2.8 2 H7.5"
            stroke="currentColor"
            strokeWidth={1.2}
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

function DashedDivider(): ReactNode {
  return (
    <div
      aria-hidden
      className="mt-3 h-px"
      style={{
        backgroundImage:
          'repeating-linear-gradient(90deg, var(--color-border-hairline) 0 4px, transparent 4px 8px)',
      }}
    />
  );
}

/**
 * Parse mono-formatted numeric strings like "50.0000" / "136.4400" into a
 * ratio unlocked/(unlocked+vesting). Returns NaN-safe fallback 1.
 */
function safeRatio(unlocked: string, vesting: string): number {
  const u = Number(unlocked.replace(/[,\s]/g, ''));
  const v = Number(vesting.replace(/[,\s]/g, ''));
  if (!Number.isFinite(u) || !Number.isFinite(v) || u + v === 0) return 1;
  return u / (u + v);
}
